import { KNOWLEDGE_ITEM_TYPES } from '../knowledgeItems/knowledgeItemModel';
import { isProfileField } from './profileFieldVocabulary';
import { COMMUNICATION_DOMAIN_ID } from './communicationDomain';
import { WHAT_HELPS_DOMAIN_ID } from './whatHelpsDomain';
import { WATCH_FOR_DOMAIN_ID } from './watchForDomain';
import { DAILY_LIFE_DOMAIN_ID } from './dailyLifeDomain';
import { SAFETY_DOMAIN_ID } from './safetyDomain';
import { HEALTH_CARE_DOMAIN_ID } from './healthCareDomain';
import { GOALS_DOMAIN_ID } from './goalsDomain';
import { PROFILE_PLACEMENT_CHAPTERS } from './profilePlacementMetadata';
import { PROFILE_CHAPTER_TITLES } from '../../constants/profileChapterTitles';

export { PROFILE_PLACEMENT_CHAPTERS } from './profilePlacementMetadata';

const ALL_KNOWLEDGE_TYPES = Object.freeze(Object.values(KNOWLEDGE_ITEM_TYPES));

const freezePlacement = ({ aliases = [], allowedKnowledgeTypes = [], acceptedProfileFields = [], ...placement }) => Object.freeze({
  ...placement,
  aliases: Object.freeze(aliases),
  acceptedProfileFields: Object.freeze(acceptedProfileFields),
  allowedKnowledgeTypes: Object.freeze(allowedKnowledgeTypes),
});

export const PROFILE_KNOWLEDGE_CHAPTERS = Object.freeze([
  Object.freeze({ chapterId: PROFILE_PLACEMENT_CHAPTERS.COMMUNICATION, profileViewId: 'communication', domainKey: 'communication', label: PROFILE_CHAPTER_TITLES.COMMUNICATION }),
  Object.freeze({ chapterId: PROFILE_PLACEMENT_CHAPTERS.WHAT_HELPS, profileViewId: 'whatHelps', domainKey: 'whatHelpsComfort', label: PROFILE_CHAPTER_TITLES.WHAT_HELPS }),
  Object.freeze({ chapterId: PROFILE_PLACEMENT_CHAPTERS.WATCH_FOR, profileViewId: 'watchFor', domainKey: 'watchFor', label: PROFILE_CHAPTER_TITLES.WATCH_FOR }),
  Object.freeze({ chapterId: PROFILE_PLACEMENT_CHAPTERS.DAILY_LIFE, profileViewId: 'dailyLife', domainKey: 'dailyLifeRoutines', label: PROFILE_CHAPTER_TITLES.DAILY_LIFE }),
  Object.freeze({ chapterId: PROFILE_PLACEMENT_CHAPTERS.SAFETY, profileViewId: 'safety', domainKey: 'safety', label: PROFILE_CHAPTER_TITLES.SAFETY }),
  Object.freeze({ chapterId: PROFILE_PLACEMENT_CHAPTERS.HEALTH_CARE, profileViewId: 'healthCare', domainKey: 'healthCare', label: PROFILE_CHAPTER_TITLES.HEALTH_CARE }),
]);

// This describes the placement vocabulary already consumed by today's Profile
// Contract domains. It does not decide whether an individual fact is current,
// reviewed, durable, or textually relevant; those eligibility checks remain in
// the existing domain resolvers.
export const PROFILE_PLACEMENTS = Object.freeze({
  [PROFILE_PLACEMENT_CHAPTERS.COMMUNICATION]: freezePlacement({
    chapterId: PROFILE_PLACEMENT_CHAPTERS.COMMUNICATION,
    domainId: COMMUNICATION_DOMAIN_ID,
    domainKey: 'communication',
    profileField: 'communicationStyle',
    acceptedProfileFields: ['communicationStyle', 'communicationNotes'],
    allowedKnowledgeTypes: ALL_KNOWLEDGE_TYPES,
    eligibilityMode: 'profile_field',
    aliases: ['communication_style', 'communicationStyle', COMMUNICATION_DOMAIN_ID],
  }),
  [PROFILE_PLACEMENT_CHAPTERS.WHAT_HELPS]: freezePlacement({
    chapterId: PROFILE_PLACEMENT_CHAPTERS.WHAT_HELPS,
    domainId: WHAT_HELPS_DOMAIN_ID,
    domainKey: 'whatHelpsComfort',
    profileField: 'whatHelps',
    acceptedProfileFields: ['whatHelps', 'thingsThatUsuallyHelp', 'familySupportTips'],
    allowedKnowledgeTypes: ALL_KNOWLEDGE_TYPES,
    eligibilityMode: 'profile_field',
    aliases: ['whatHelps', 'what_helps_comfort', 'whatHelpsComfort', WHAT_HELPS_DOMAIN_ID],
  }),
  [PROFILE_PLACEMENT_CHAPTERS.WATCH_FOR]: freezePlacement({
    chapterId: PROFILE_PLACEMENT_CHAPTERS.WATCH_FOR,
    domainId: WATCH_FOR_DOMAIN_ID,
    domainKey: 'watchFor',
    profileField: 'earlyWarningSigns',
    acceptedProfileFields: ['earlyWarningSigns', 'thingsToAvoid', 'safetyConsiderations'],
    allowedKnowledgeTypes: ALL_KNOWLEDGE_TYPES,
    eligibilityMode: 'profile_field_and_content',
    aliases: ['watchFor', 'things_to_watch_for', WATCH_FOR_DOMAIN_ID],
  }),
  [PROFILE_PLACEMENT_CHAPTERS.DAILY_LIFE]: freezePlacement({
    chapterId: PROFILE_PLACEMENT_CHAPTERS.DAILY_LIFE,
    domainId: DAILY_LIFE_DOMAIN_ID,
    domainKey: 'dailyLifeRoutines',
    profileField: null,
    allowedKnowledgeTypes: [
      KNOWLEDGE_ITEM_TYPES.ROUTINE,
      KNOWLEDGE_ITEM_TYPES.SLEEP,
      KNOWLEDGE_ITEM_TYPES.FEEDING,
    ],
    eligibilityMode: 'knowledge_type_and_content',
    aliases: ['dailyLife', 'daily_life_routines', 'dailyLifeRoutines', DAILY_LIFE_DOMAIN_ID],
  }),
  [PROFILE_PLACEMENT_CHAPTERS.SAFETY]: freezePlacement({
    chapterId: PROFILE_PLACEMENT_CHAPTERS.SAFETY,
    domainId: SAFETY_DOMAIN_ID,
    domainKey: 'safety',
    profileField: 'safetyConsiderations',
    acceptedProfileFields: ['safetyConsiderations'],
    allowedKnowledgeTypes: [
      KNOWLEDGE_ITEM_TYPES.SAFETY,
      KNOWLEDGE_ITEM_TYPES.RISK,
      KNOWLEDGE_ITEM_TYPES.INSTRUCTION,
      KNOWLEDGE_ITEM_TYPES.PROFILE,
      KNOWLEDGE_ITEM_TYPES.FEEDING,
      KNOWLEDGE_ITEM_TYPES.MEDICATION,
    ],
    eligibilityMode: 'knowledge_type_and_content',
    aliases: [SAFETY_DOMAIN_ID],
  }),
  [PROFILE_PLACEMENT_CHAPTERS.HEALTH_CARE]: freezePlacement({
    chapterId: PROFILE_PLACEMENT_CHAPTERS.HEALTH_CARE,
    domainId: HEALTH_CARE_DOMAIN_ID,
    domainKey: 'healthCare',
    profileField: null,
    allowedKnowledgeTypes: [
      KNOWLEDGE_ITEM_TYPES.FEEDING,
      KNOWLEDGE_ITEM_TYPES.SLEEP,
      KNOWLEDGE_ITEM_TYPES.BASELINE,
      KNOWLEDGE_ITEM_TYPES.CONDITION,
      KNOWLEDGE_ITEM_TYPES.INSTRUCTION,
      KNOWLEDGE_ITEM_TYPES.SUPPORT,
      KNOWLEDGE_ITEM_TYPES.ROUTINE,
    ],
    eligibilityMode: 'knowledge_type_and_content',
    aliases: ['health', 'healthCare', 'health_and_care', HEALTH_CARE_DOMAIN_ID],
  }),
  [PROFILE_PLACEMENT_CHAPTERS.GOALS]: freezePlacement({
    chapterId: PROFILE_PLACEMENT_CHAPTERS.GOALS,
    domainId: GOALS_DOMAIN_ID,
    domainKey: 'goals',
    profileField: null,
    allowedKnowledgeTypes: [],
    eligibilityMode: 'structured_goals',
    aliases: ['goal', GOALS_DOMAIN_ID],
  }),
});

const normalizeChapterId = (value = '') => String(value || '').trim();
const normalizeAliasKey = (value = '') => normalizeChapterId(value).toLowerCase().replace(/[\s-]+/g, '_');

const placementByAlias = new Map();

Object.values(PROFILE_PLACEMENTS).forEach((placement) => {
  [placement.chapterId, placement.domainKey, placement.domainId, ...placement.aliases].forEach((alias) => {
    const key = normalizeAliasKey(alias);
    if (!key) return;
    const existing = placementByAlias.get(key);
    if (existing && existing.chapterId !== placement.chapterId) {
      throw new Error(`Conflicting Profile placement alias: ${alias}`);
    }
    placementByAlias.set(key, placement);
  });
});

export const getProfilePlacement = (chapterId = '') => (
  placementByAlias.get(normalizeAliasKey(chapterId)) || null
);

export const isRecognizedProfilePlacementField = (profileField = '') => (
  isProfileField(normalizeChapterId(profileField))
);

export const getCurrentKnowledgeForProfilePlacement = (items = [], chapterId = '') => {
  const placement = getProfilePlacement(chapterId);
  if (!placement || placement.eligibilityMode === 'structured_goals') return [];

  return items.filter((item) => {
    if (item.factStatus === 'outdated' || item.status === 'outdated' || item.status === 'archived' || item.archived === true) return false;
    const itemPlacement = getProfilePlacement(item.profileChapterId);
    if (itemPlacement) return itemPlacement.chapterId === placement.chapterId;
    if (item.profileField) {
      return placement.acceptedProfileFields.includes(item.profileField)
        || placement.profileField === item.profileField;
    }
    return placement.allowedKnowledgeTypes.includes(item.knowledgeType || item.factType);
  });
};
